import React from "react";
import avatar from "../../../assets/usericon.png";

/* ─── SVG Icons (local to EditProfile) ─────────────────────────── */

function EditPenIcon() {
    return (
        <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z"
            />
        </svg>
    );
}

function WarningTriangleIcon() {
    return (
        <svg
            className="w-5 h-5 text-yellow-100 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.98-.833-2.75 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
        </svg>
    );
}

/* ─── Alert Banners ────────────────────────────────────────────── */

export function AlertBanner({ message, type }) {
    if (!message) return null;
    const isSuccess = type === "success";
    const colorClasses = isSuccess
        ? "bg-green-900/50 border border-green-700 text-green-300"
        : "bg-red-900/50 border border-red-700 text-red-300";

    return (
        <div
            className={`${colorClasses} px-4 py-3 rounded-lg mb-6 animate-fadeInOut`}
        >
            {message}
        </div>
    );
}

/* ─── Profile Picture Card (Left Column) ──────────────────────── */

export function ProfilePictureCard({
    name,
    email,
    photoURL,
    previewURL,
    selectedFile,
    uploading,
    language,
    onFileSelect,
    onImageUpload,
}) {
    return (
        <div className="lg:col-span-1 space-y-8">
            <div className="bg-slate-200 border border-white/10 rounded-lg p-6 text-center sticky top-8 shadow-lg">
                {/* Avatar with edit overlay */}
                <div className="relative inline-block mb-4">
                    <img
                        src={previewURL || photoURL || avatar}
                        alt="Profile"
                        className="w-32 h-32 rounded-full object-cover border-4 border-orange-500 shadow-lg"
                    />
                    <label
                        htmlFor="profileImage"
                        className="absolute bottom-0 right-0 bg-gray-900 p-2 rounded-full cursor-pointer hover:bg-gray-700 transition-colors"
                    >
                        <EditPenIcon />
                        <input
                            type="file"
                            id="profileImage"
                            accept="image/*"
                            onChange={onFileSelect}
                            className="hidden"
                        />
                    </label>
                </div>

                <h2 className="text-xl font-bold text-gray-800 truncate">
                    {name || "User"}
                </h2>
                <p className="text-sm text-gray-700 truncate">{email}</p>

                {/* Upload prompt (only visible when file selected) */}
                {selectedFile && (
                    <div className="mt-4 p-3 bg-blue-900/50 rounded-md border border-blue-700">
                        <p className="text-sm text-blue-300 mb-2 truncate">
                            {selectedFile.name}
                        </p>
                        <button
                            type="button"
                            onClick={onImageUpload}
                            disabled={uploading}
                            className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {uploading
                                ? language === "zh"
                                    ? "上傳中..."
                                    : "Uploading..."
                                : language === "zh"
                                  ? "上傳圖片"
                                  : "Upload Image"}
                        </button>
                    </div>
                )}

                <p className="mt-2 text-xs text-gray-500">
                    {language === "zh"
                        ? "支援 JPG, PNG. 最大 5MB."
                        : "JPG, PNG supported. Max 5MB."}
                </p>
            </div>
        </div>
    );
}

/* ─── Personal Information Form ────────────────────────────────── */

export function PersonalInfoForm({
    name,
    setName,
    email,
    userRoles,
    updateMessage,
    loading,
    language,
    onUpdate,
    onCancel,
}) {
    return (
        <div className="bg-slate-200 border border-white/10 rounded-lg shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6">
                {language === "zh" ? "個人資訊" : "Personal Information"}
            </h3>

            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Display Name */}
                    <div>
                        <label
                            htmlFor="displayName"
                            className="block text-sm font-medium text-gray-400 mb-2"
                        >
                            {language === "zh" ? "顯示名稱" : "Display Name"}
                        </label>
                        <input
                            type="text"
                            id="displayName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={
                                language === "zh"
                                    ? "輸入您的顯示名稱"
                                    : "Enter your display name"
                            }
                            className="w-full px-4 py-2 bg-slate-100 text-gray-800 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                        />
                    </div>
                    {/* Email (read-only) */}
                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-400 mb-2"
                        >
                            {language === "zh" ? "電子郵件" : "Email Address"}
                        </label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            disabled
                            className="w-full px-4 py-2 bg-gray-400 border border-gray-700 rounded-md shadow-sm text-gray-500 cursor-not-allowed"
                        />
                    </div>
                </div>

                {/* User Roles */}
                <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                        {language === "zh" ? "用戶角色" : "User Role"}
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {userRoles && userRoles.length > 0 ? (
                            userRoles.map((role) => (
                                <span
                                    key={role}
                                    className="inline-block bg-orange-500 border border-orange-500/50 px-3 py-1.5 rounded-full text-xs text-slate-100 font-medium"
                                >
                                    {role}
                                </span>
                            ))
                        ) : (
                            <p className="text-sm text-gray-700">
                                No roles assigned.
                            </p>
                        )}
                    </div>
                </div>

                {/* Update message */}
                {updateMessage && (
                    <div
                        className={`p-3 rounded-md text-sm animate-fadeInOut ${
                            updateMessage.includes("successfully")
                                ? "bg-green-900/50 border border-green-700 text-green-300"
                                : "bg-red-900/50 border border-red-700 text-red-300"
                        }`}
                    >
                        {updateMessage}
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end space-x-4 pt-4 border-t border-white/10">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-6 py-2 bg-gray-200 text-gray-600 rounded-md hover:bg-red-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
                    >
                        {language === "zh" ? "取消" : "Cancel"}
                    </button>
                    <button
                        type="button"
                        onClick={onUpdate}
                        disabled={loading}
                        className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading
                            ? language === "zh"
                                ? "更新中..."
                                : "Updating..."
                            : language === "zh"
                              ? "更新個人資料"
                              : "Update Profile"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Password Change Section ──────────────────────────────────── */

export function PasswordChangeSection({
    showPasswordSection,
    togglePasswordSection,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    passwordLoading,
    passwordMessage,
    language,
    onPasswordChange,
}) {
    return (
        <div className="bg-slate-200 border border-white/10 rounded-lg shadow-lg px-8 py-2 my-8">
            {/* Header with toggle */}
            <div className="flex items-center justify-between my-6">
                <h3 className="text-xl font-bold text-gray-800">
                    {language === "zh" ? "密碼設置" : "Password Settings"}
                </h3>
                <button
                    type="button"
                    onClick={togglePasswordSection}
                    className={`px-4 py-2 text-sm text-gray-600 rounded-md ${
                        showPasswordSection
                            ? "bg-gray-200 hover:bg-red-500 hover:text-white transition-all"
                            : "bg-gray-300 hover:bg-gray-400 transition-colors"
                    }`}
                >
                    {showPasswordSection
                        ? language === "zh"
                            ? "取消"
                            : "Cancel"
                        : language === "zh"
                          ? "更改密碼"
                          : "Change Password"}
                </button>
            </div>

            {showPasswordSection && (
                <div className="space-y-6">
                    {/* Requirements banner */}
                    <div className="flex items-start space-x-3 bg-yellow-500/90 border border-yellow-600/50 rounded-lg p-4">
                        <WarningTriangleIcon />
                        <div className="text-sm text-yellow-100">
                            <p className="font-medium mb-1">
                                {language === "zh"
                                    ? "密码要求："
                                    : "Password Requirements:"}
                            </p>
                            <ul className="list-disc list-inside text-xs">
                                <li>
                                    {language === "zh"
                                        ? "至少6个字符"
                                        : "At least 6 characters"}
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Password fields */}
                    <div className="space-y-4">
                        <div>
                            <label
                                htmlFor="currentPassword"
                                className="block text-sm font-medium text-gray-400 mb-2"
                            >
                                {language === "zh"
                                    ? "当前密码"
                                    : "Current Password"}
                            </label>
                            <input
                                type="password"
                                id="currentPassword"
                                value={currentPassword}
                                onChange={(e) =>
                                    setCurrentPassword(e.target.value)
                                }
                                placeholder={
                                    language === "zh"
                                        ? "输入当前密码"
                                        : "Enter current password"
                                }
                                className="w-full px-4 py-2 bg-slate-100 text-gray-800 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label
                                    htmlFor="newPassword"
                                    className="block text-sm font-medium text-gray-400 mb-2"
                                >
                                    {language === "zh"
                                        ? "新密码"
                                        : "New Password"}
                                </label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) =>
                                        setNewPassword(e.target.value)
                                    }
                                    placeholder={
                                        language === "zh"
                                            ? "输入新密码"
                                            : "Enter new password"
                                    }
                                    className="w-full px-4 py-2 bg-slate-100 text-gray-800 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="confirmPassword"
                                    className="block text-sm font-medium text-gray-400 mb-2"
                                >
                                    {language === "zh"
                                        ? "确认新密码"
                                        : "Confirm New Password"}
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) =>
                                        setConfirmPassword(e.target.value)
                                    }
                                    placeholder={
                                        language === "zh"
                                            ? "再次输入新密码"
                                            : "Enter new password again"
                                    }
                                    className="w-full px-4 py-2 bg-slate-100 text-gray-800 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                                />
                            </div>
                        </div>

                        {/* Password status message */}
                        {passwordMessage?.text && (
                            <div
                                className={`p-3 rounded-md text-sm ${
                                    passwordMessage.type === "success"
                                        ? "bg-green-900/50 border border-green-700 text-green-300"
                                        : "bg-red-900/50 border border-red-700 text-red-300"
                                }`}
                            >
                                {passwordMessage.text}
                            </div>
                        )}

                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={onPasswordChange}
                                disabled={
                                    passwordLoading ||
                                    !currentPassword ||
                                    !newPassword ||
                                    !confirmPassword
                                }
                                className="mb-6 px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {passwordLoading
                                    ? language === "zh"
                                        ? "更新中..."
                                        : "Updating..."
                                    : language === "zh"
                                      ? "更新密码"
                                      : "Update Password"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
